import React, { useContext } from 'react';
import { CategoryQueryContext, CategoryQueryProvider } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import { useSetResolved } from 'src/core/engine/router/useSetResolved';
import { CategoryHeader } from 'src/components/CategoryListingPage/CategoryHeader';
import { ProductListToolbar } from 'src/components/CategoryListingPage/ProductListToolbar';
import { ProductListWrapper } from 'src/components/CategoryListingPage/ProductListWrapper';
import { Container, TwoCol, Wrapper } from 'src/components/Layout';
import { FilterModalProvider } from 'src/components/CategoryListingPage/FilterModalContext';
import { LayeredNav } from 'src/components/LayeredNav/LayeredNav';
import {
    AggregationQueryContext,
    AggregationQueryProvider,
} from 'src/components/CategoryListingPage/AggregationQueryProvider';
import {
    ProductListQueryContext,
    ProductListQueryProvider,
} from 'src/components/CategoryListingPage/ProductListQueryProvider';
import { DefaultMeta } from 'src/components/AppShell/Head';
import { ErrorOrOfflineConnected } from 'src/components/Error/ErrorOrOffline';
import { useLayeredNav } from 'src/components/LayeredNav/hooks/useLayeredNav';
import '../../scss/_root-component.scss';
import { SELECTORS } from 'src/components/LayeredNav/layeredNav.types';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';
import { CategoryRootSkeleton, Line } from 'src/elements/Skeletons/Skeletons';
import {
    filterAggregations,
    filterMappedAggregations,
} from 'src/RootComponents/Category/utils/filterMappedAggregations';
import { MobileCreditBalance } from 'src/components/Customer/MobileCreditBalance';
import classes from 'src/components/Customer/MobileCreditBalance.scss';
import { useSetPopupPageCategory } from 'src/components/PopupBanner/PopupPageContext';
import { CategoryFaq } from 'src/components/CategoryFaq/CategoryFaq';

type ProductProps = {
    id: number;
};

export const CategoryRoot: React.FC<ProductProps> = React.memo((props) => {
    useSetResolved();
    return (
        <Wrapper element="section" variant="widget-wrapper">
            <Container>
                <CategoryQueryProvider id={props.id}>
                    <AggregationQueryProvider id={props.id}>
                        <ProductListQueryProvider id={props.id}>
                            <CategoryRootInner />
                        </ProductListQueryProvider>
                    </AggregationQueryProvider>
                </CategoryQueryProvider>
            </Container>
        </Wrapper>
    );
});

const CategoryRootInner = React.memo(() => {
    const {
        loading,
        name,
        meta_title,
        meta_description,
        description,
        breadcrumbs,
        urlKey,
        breadcrumbLabel,
        category_id,
        parentCategoryIds,
        faq,
    } = useContext(CategoryQueryContext);

    useSetPopupPageCategory(category_id, parentCategoryIds);
    const { mappedAggregations } = useContext(AggregationQueryContext);
    const filteredAggregations = filterMappedAggregations(mappedAggregations);
    const { parsedCategoryProduct } = useContext(ProductListQueryContext);
    const { isMob } = useBreakpointMatch();
    const { ref, onRemove, onRemoveAll, onChange, selection$, filtersSelected } = useLayeredNav(
        filteredAggregations,
        isMob ? SELECTORS.MODAL : 'html',
    );

    parsedCategoryProduct.mappedAggregations = filteredAggregations;
    parsedCategoryProduct.aggregations = filterAggregations(parsedCategoryProduct.aggregations);
    parsedCategoryProduct.highlightedAggregations = filterAggregations(parsedCategoryProduct.highlightedAggregations);

    if (loading) {
        return <CategoryRootSkeleton />;
    }
    const meta = {
        title: meta_title || name || 'unknown',
        description: meta_description || name || 'unknown',
    };

    return (
        <FilterModalProvider>
            <DefaultMeta data={meta} withoutPrefix />
            <MobileCreditBalance
                showTopDivider
                showBottomDivider
                contentCentered
                smallPadding
                className={classes.negativeMarginTop}
            />

            <TwoCol
                left={
                    <LayeredNav
                        mappedAggregations={filteredAggregations}
                        onRemoveAll={onRemoveAll}
                        onRemove={onRemove}
                        isMob={isMob}
                        ref={ref}
                        selection$={selection$}
                        onChange={onChange}
                        filtersSelected={filtersSelected}
                        parsedCategoryProduct={parsedCategoryProduct}
                        urlKey={urlKey}
                    />
                }
                main={
                    <div itemScope itemType="https://schema.org/ItemList">
                        <CategoryHeader
                            title={name}
                            description={description}
                            breadcrumbs={breadcrumbs}
                            onRemoveAll={onRemoveAll}
                            filtersSelected={filtersSelected}
                            breadcrumbLabel={breadcrumbLabel}
                            urlKey={urlKey}
                        />
                        <ProductListToolbar
                            breadcrumbLabel={
                                Boolean(breadcrumbLabel && breadcrumbLabel !== '')
                                    ? breadcrumbLabel
                                    : breadcrumbs?.subcategoryName
                            }
                        />
                        <CategoryProducts selection$={selection$} onChange={onChange} onRemove={onRemove} />
                        {faq?.length > 0 && <CategoryFaq title={name} faq={faq} />}
                        {/* {productListingDescription && treatment === 'on' && (
                            <CategoryTextBlock html={productListingDescription} />
                        )} */}
                    </div>
                }
            />
        </FilterModalProvider>
    );
});

const CategoryProducts: React.FC<any> = React.memo(({ onChange, selection$ }) => {
    const { loading: categoryLoading, urlKey, title } = useContext(CategoryQueryContext);
    const { loading: productsLoading, error: listError } = useContext(ProductListQueryContext);
    const { loading: aggregationsLoading, error: aggError } = useContext(AggregationQueryContext);

    if (listError || aggError) {
        return <ErrorOrOfflineConnected message="These was a problem loading this category" />;
    }

    if (productsLoading || categoryLoading || aggregationsLoading) {
        return (
            <div>
                <Line />
            </div>
        );
    }

    return <ProductListWrapper title={title} urlKey={urlKey} onChange={onChange} selection$={selection$} />;
});

export default CategoryRoot;
